'use client';

import React from 'react';

interface DetailedService {
  id: string;
  title: string;
  description: string;
  whatIncluded: string;
  toolsAndMethods: string;
  phoenixBenefits: string;
  desertClimateCare: string;
  image: string;
  imageAlt: string;
}

interface DetailedServiceSectionProps {
  services: DetailedService[];
}

export default function DetailedServiceSection({ services }: DetailedServiceSectionProps) {
  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-headline font-bold text-4xl md:text-5xl text-primary mb-4">
            Our Specialized Services
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto">
            Expert patio cleaning and restoration services designed specifically for Phoenix's unique desert environment
          </p>
        </div>

        <div className="space-y-24">
          {services.map((service, index) => (
            <div 
              key={service.id}
              id={service.id}
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
            >
              <div className="flex-1">
                <img
                  src={service.image}
                  alt={service.imageAlt}
                  className="w-full h-[400px] object-cover rounded-2xl shadow-2xl"
                />
              </div>

              <div className="flex-1 space-y-6">
                <h3 className="font-headline font-bold text-3xl md:text-4xl text-primary">
                  {service.title}
                </h3>
                
                <p className="font-body text-lg text-foreground leading-relaxed">
                  {service.description}
                </p>

                <div className="space-y-4">
                  <h4 className="font-headline font-semibold text-xl text-primary">
                    What's Included
                  </h4>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    {service.whatIncluded}
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="font-headline font-semibold text-xl text-primary">
                    Tools & Methods
                  </h4>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    {service.toolsAndMethods}
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="font-headline font-semibold text-xl text-primary">
                    Phoenix Homeowner Benefits
                  </h4>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    {service.phoenixBenefits}
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="font-headline font-semibold text-xl text-primary">
                    Desert Climate Care
                  </h4>
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    {service.desertClimateCare}
                  </p>
                </div>

                <div className="pt-6">
                  <a
                    href="tel:+16027773005"
                    className="inline-block bg-accent hover:bg-accent/90 text-accent-foreground font-headline font-semibold px-8 py-4 rounded-lg transition-colors duration-200"
                  >
                    Get Free Quote - (602) 777-3005
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}